import { Box, Flex, Group, Image, Stack } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { Button } from '@repo/foundations';
import { useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser, useSuspenseFeatureAccess } from '../../../api/hooks';
import { AuthCard } from '../../../components/Auth/AuthCard';
import { AuthFooter } from '../../../components/Auth/AuthLayouts/AuthFooter';
import { AuthLayoutBase } from '../../../components/Auth/AuthLayouts/AuthLayoutBase';
import { FullWidthLoadingSpinner } from '../../../components/LoadingSpinner';
import { useAiWorkspaceSettingEnabled } from '../../../hooks/useAiWorkspaceSettingEnabled';
import { OnboardingStepper } from '../SecodaLiteOnboarding/OnboardingStepper';
import OnboardingViewerContent from './OnboardingViewerContent';
import OnboardingAskAiPage from './components/OnboardingAskAiPage';
import { ONBOARDING_PAGES, OnboardingStep } from './constants';

interface OnboardingViewerWelcomePageProps {
	startStep?: 0 | 1 | 2 | 3;
	forceAskAiLoading?: boolean;
}

const AI_ONBOARDING_PAGES = [
	OnboardingStep.Welcome,
	OnboardingStep.GainMoreContext,
	OnboardingStep.AskAi,
];

const NON_AI_ONBOARDING_PAGES = [
	OnboardingStep.Welcome,
	OnboardingStep.NoMoreRepetitiveQuestions,
	OnboardingStep.GainMoreContext,
];

export default function OnboardingViewerWelcomePage({
	startStep,
	forceAskAiLoading = false,
}: OnboardingViewerWelcomePageProps) {
	const [step, setStep] = useState<number>(startStep || 0);
	const { user, isLoading, workspace } = useAuthUser();
	const aiWorkspaceSettingEnabled = useAiWorkspaceSettingEnabled();
	const navigate = useNavigate();
	const { secodaCoreAccess } = useSuspenseFeatureAccess();

	const stepsOffset = secodaCoreAccess ? 0 : 5;

	const steps = aiWorkspaceSettingEnabled
		? AI_ONBOARDING_PAGES
		: NON_AI_ONBOARDING_PAGES;

	// eslint-disable-next-line no-use-effect/no-use-effect
	useLayoutEffect(() => {
		trackEvent(
			`viewer_onboarding/post_login_education_page_${step}/view`,
			{},
			user,
			workspace
		);
	}, [step, user, workspace]);

	const handleNext = () => {
		if (step < steps.length - 1) {
			setStep(step + 1);
		} else {
			navigate('/');
		}
	};

	if (isLoading) {
		return <FullWidthLoadingSpinner />;
	}

	const isAskAiStep =
		aiWorkspaceSettingEnabled && steps[step] === OnboardingStep.AskAi;

	const onboardingImageData = ONBOARDING_PAGES[steps[step]];

	return (
		<AuthLayoutBase>
			<AuthCard width="800px" p="0">
				<Group align="stretch" spacing="0" noWrap>
					<Stack
						spacing={0}
						p="2xl"
						justify="space-between"
						style={{
							flex: 1,
						}}
					>
						<Stack spacing="3xl" h="100%" justify="center">
							<Flex
								gap="xl"
								direction="column"
								h={aiWorkspaceSettingEnabled ? undefined : '200px'}
							>
								{isAskAiStep ? (
									<OnboardingAskAiPage
										user={user}
										workspace={workspace}
										forceLoading={forceAskAiLoading}
									/>
								) : (
									<OnboardingViewerContent
										page={onboardingImageData}
										firstName={user?.first_name || ''}
										workspaceName={workspace.name}
									/>
								)}
							</Flex>
						</Stack>
					</Stack>
					{!isAskAiStep && (
						<Box maw={400} mah={539}>
							<Image
								src={onboardingImageData.image.src}
								alt={onboardingImageData.image.alt}
								fit="contain"
								width={400}
								height={539}
								style={{
									objectPosition: 'top right',
								}}
							/>
						</Box>
					)}
				</Group>
				<AuthFooter mt="0" denyMargin={false}>
					<Group position="apart" py="md" px="2xl">
						<OnboardingStepper
							step={step + stepsOffset}
							totalSteps={steps.length + stepsOffset}
						/>
						<Group spacing="xs" position="right">
							<Button
								variant="default"
								onClick={() => step > 0 && setStep(step - 1)}
								disabled={step === 0}
							>
								Back
							</Button>
							{!(step === steps.length - 1 && aiWorkspaceSettingEnabled) && (
								<Button onClick={handleNext} variant="primary">
									Continue
								</Button>
							)}
						</Group>
					</Group>
				</AuthFooter>
			</AuthCard>
		</AuthLayoutBase>
	);
}
